
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import React from "react";
import { GetStaticPaths, GetStaticProps } from "next";
import { useFela } from "react-fela";

import { useTheme } from "@/hooks/useTheme";
import { Grid } from "@/components/Grid";
import { Link } from "@/components/Link";
import { BlogPostHead } from "@/containers/Blog/BlogPostHead";
import { formatPostDate } from "@/containers/Blog/BlogPostCard";
import {
  getAllPostSlugs,
  getPostBySlug,
  IBlogPost,
} from "@/services/wordpress";

interface IProps {
  post: IBlogPost;
}

const BlogPostPage: React.FC<IProps> = ({ post }) => {
  const { css } = useFela();
  const { colors, bp } = useTheme();

  return (
    <>
      <BlogPostHead post={post} />

      <Grid customStyle={{ flexDirection: "column", paddingBottom: 80 }}>
        <article
          className={css({
            maxWidth: 760,
            width: "100%",
            margin: "0 auto",
          })}
        >
          <header className={css({ marginTop: 40, marginBottom: 32 })}>
            {post.categories.length > 0 && (
              <Link
                href={`/blog/category/${post.categories[0].slug}`}
                customStyle={{
                  fontSize: 13,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  color: colors.GRADIENT_PURPLE,
                  textDecoration: "none",
                }}
              >
                {post.categories[0].name}
              </Link>
            )}

            <h1
              className={css({
                fontSize: 32,
                lineHeight: 1.3,
                color: colors.CHARCOAL,
                margin: "12px 0 16px",
                [bp.FROM_DESKTOP]: { fontSize: 40 },
              })}
            >
              {post.title}
            </h1>

            <p
              className={css({
                display: "flex",
                alignItems: "center",
                gap: 8,
                color: colors.GRAY,
                fontSize: 15,
                margin: 0,
              })}
            >
              {post.authorAvatar && (
                <img
                  src={post.authorAvatar}
                  alt={post.authorName || "Author"}
                  width={28}
                  height={28}
                  className={css({ borderRadius: "50%", display: "block" })}
                />
              )}
              {post.authorName && <span>{post.authorName}</span>}
              <span aria-hidden="true">·</span>
              <time dateTime={post.date}>{formatPostDate(post.date)}</time>
              {post.modified.slice(0, 10) !== post.date.slice(0, 10) && (
                <span>· Updated {formatPostDate(post.modified)}</span>
              )}
            </p>
          </header>

          {post.featuredImage && (
            <img
              src={post.featuredImage.url}
              alt={post.featuredImage.alt || post.title}
              width={post.featuredImage.width ?? undefined}
              height={post.featuredImage.height ?? undefined}
              // The hero is the LCP element — load it eagerly and early.
              loading="eager"
              {...({ fetchpriority: "high" } as Record<string, string>)}
              className={css({
                width: "100%",
                height: "auto",
                borderRadius: 12,
                marginBottom: 32,
              })}
            />
          )}

          <div
            className={css({
              color: colors.CHARCOAL,
              fontSize: 17,
              lineHeight: 1.75,
              nested: {
                "& h2": {
                  fontSize: 26,
                  lineHeight: 1.35,
                  margin: "40px 0 16px",
                },
                "& h3": {
                  fontSize: 21,
                  lineHeight: 1.4,
                  margin: "32px 0 12px",
                },
                "& h4": { fontSize: 18, margin: "28px 0 10px" },
                "& p": { margin: "0 0 20px" },
                "& a": {
                  color: colors.GRADIENT_PURPLE,
                  textDecorationColor: colors.GRADIENT_PURPLE,
                },
                "& img": {
                  maxWidth: "100%",
                  height: "auto",
                  borderRadius: 8,
                },
                "& figure": { margin: "28px 0" },
                "& figcaption": {
                  fontSize: 14,
                  color: colors.GRAY,
                  textAlign: "center",
                  marginTop: 8,
                },
                "& ul": { paddingLeft: 24, margin: "0 0 20px" },
                "& ol": { paddingLeft: 24, margin: "0 0 20px" },
                "& li": { marginBottom: 8 },
                "& blockquote": {
                  borderLeft: `4px solid ${colors.GRADIENT_PURPLE}`,
                  margin: "28px 0",
                  padding: "4px 0 4px 20px",
                  color: colors.DARK_GREY,
                  fontStyle: "italic",
                },
                "& pre": {
                  background: colors.CHARCOAL,
                  color: colors.CULTURED,
                  borderRadius: 8,
                  padding: 20,
                  overflowX: "auto",
                  fontSize: 14,
                },
                "& code": { fontSize: "0.95em" },
                "& table": {
                  width: "100%",
                  borderCollapse: "collapse",
                  margin: "28px 0",
                  fontSize: 15,
                },
                "& th": {
                  border: `1px solid ${colors.STARDUST}`,
                  padding: "10px 12px",
                  background: colors.CULTURED,
                  textAlign: "left",
                },
                "& td": {
                  border: `1px solid ${colors.STARDUST}`,
                  padding: "10px 12px",
                },
                "& iframe": { maxWidth: "100%" },
              },
            })}
            // WP-rendered post HTML, same markup WordPress served directly.
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: post.contentHtml }}
          />

          <footer className={css({ marginTop: 48 })}>
            <Link
              href="/blog"
              customStyle={{
                color: colors.GRADIENT_PURPLE,
                textDecoration: "none",
                fontWeight: 700,
              }}
            >
              ← Back to all posts
            </Link>
          </footer>
        </article>
      </Grid>
    </>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const slugs = await getAllPostSlugs();

  return {
    paths: slugs.map(slug => ({ params: { slug } })),
    fallback: "blocking",
  };
};

 const _getStaticProps: GetStaticProps<IProps> = async ({ params }) => {
  const slug = params?.slug;
  if (typeof slug !== "string") {
    return { notFound: true };
  }

  const post = await getPostBySlug(slug);
  if (!post) {
    return { notFound: true, revalidate: 300 };
  }

  return {
    props: { post },
    revalidate: 3600,
  };
};

export default BlogPostPage;


// @ts-ignore
    export async function getStaticProps(ctx) {
// @ts-ignore
        let res = _getStaticProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/blog/[slug]',
// @ts-ignore
              loaderName: 'getStaticProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  